import {
  ChevronDown,
  Eye,
  Loader2,
  Settings,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ACTIVE_ADS_PLATFORM } from '@/lib/adsPlatform';
import { canAccessInternalAdsTools } from '@/lib/adsToolsAccess';
import { useAdsViewStore } from '@/stores/useAdsViewStore';
import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  getKpiDashboard,
  getTenantTier,
} from '@/services/googleAdsKpiDashboardService';

// Where the persistent (real) tier is set + the OAuth connection lives.
const ADS_SETTINGS_LINK = '/workspace-settings?tab=integrations';

const TIER_LABELS: Record<number, string> = {
  1: '1 · Standard',
  2: '2 · Managed',
  3: '3 · Premium',
};

// The only ads platform wired up today; the rest are placeholders shown as
// disabled so the control communicates the roadmap. Moved here from
// `AdsPlatformSelector` (ModernMainLayout) when the top-bar controls were
// consolidated into this single popover. ACTIVE_ADS_PLATFORM is shared with
// the ops page via `@/lib/adsPlatform`.
const ADS_PLATFORM_OPTIONS = [
  { value: ACTIVE_ADS_PLATFORM, label: 'Google Ads', disabled: false },
  { value: 'meta-ads', label: 'Meta Ads (coming soon)', disabled: true },
  { value: 'linkedin-ads', label: 'LinkedIn Ads (coming soon)', disabled: true },
  { value: 'tiktok-ads', label: 'TikTok Ads (coming soon)', disabled: true },
] as const;

/**
 * Single top-bar popover for the Ads Tools pages.
 *
 * Self-guards exactly like the old `AdsPlatformSelector`: only renders on Ads
 * Tools routes, for users who can access internal ads tools, when a tenant is
 * selected. Consolidates what used to be three separate top-bar controls
 * (platform selector, view-mode toggle, client-tier selector) behind one
 * compact trigger.
 *
 * - Platform: which ad network the tools operate on (`?platform=`).
 * - View: flips the global internal/client lens (`useAdsViewStore`) so it
 *   applies across every Ads Tools page.
 * - Client tier (internal only): reads/writes the current tenant's product
 *   tier. Changing the tier invalidates the KPI dashboard so any dependent
 *   view auto-refreshes.
 */
export function AdsViewControls() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useWorkspaceStore();
  const tenantId = useTenantStore((s) => s.currentTenant);
  const viewMode = useAdsViewStore((s) => s.viewMode);
  const setViewMode = useAdsViewStore((s) => s.setViewMode);
  const previewTier = useAdsViewStore((s) => s.previewTier);
  const setPreviewTier = useAdsViewStore((s) => s.setPreviewTier);

  const isAdsToolsRoute = location.pathname.startsWith('/ads-tools');
  const enabled =
    isAdsToolsRoute && canAccessInternalAdsTools(userProfile) && !!tenantId;

  // Hooks must run unconditionally; gate the queries with `enabled` and bail on
  // render below so the self-guard still returns null off ads routes.
  const tierQuery = useQuery({
    queryKey: ['tenant-tier', tenantId],
    queryFn: () => getTenantTier(tenantId as string),
    enabled,
  });

  const currentTier = tierQuery.data?.product_tier;

  // Seed the ephemeral preview with the client's real tier the first time we
  // enter client view (so the preview starts at "what the client sees today"),
  // then let the operator change it freely. Never touches the DB.
  useEffect(() => {
    if (viewMode === 'client' && previewTier == null && currentTier != null) {
      setPreviewTier(currentTier);
    }
  }, [viewMode, previewTier, currentTier, setPreviewTier]);

  // Warm the KPI dashboard cache the moment the user enters the ads section, so
  // opening KPI Diagnostics → Dashboard finds the query already resolved or
  // in-flight. Must match the tab's DEFAULT (real-tier) query key exactly —
  // `['kpi-dashboard', tenantId, null]` — or the prefetch warms a key nothing
  // reads. staleTime dedupes; the backend's 10-min cache dedupes server-side.
  // Must run unconditionally (before the early return) to keep hook order stable.
  useEffect(() => {
    if (!isAdsToolsRoute || !canAccessInternalAdsTools(userProfile) || !tenantId)
      return;
    void queryClient.prefetchQuery({
      queryKey: ['kpi-dashboard', tenantId, null],
      queryFn: () => getKpiDashboard(tenantId),
      staleTime: 5 * 60_000,
    });
  }, [isAdsToolsRoute, userProfile, tenantId, queryClient]);

  if (!enabled) {
    return null;
  }

  const handlePlatformChange = (value: string) => {
    if (value !== ACTIVE_ADS_PLATFORM) return;
    const params = new URLSearchParams(location.search);
    params.set('platform', value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Switching lenses. Leaving client view drops the ephemeral preview so it can
  // never leak into (or be mistaken for) the real internal view.
  const handleViewChange = (mode: 'internal' | 'client') => {
    setViewMode(mode);
    if (mode === 'internal') setPreviewTier(null);
  };

  // Trigger label reflects the active lens. Internal says "all metrics" —
  // the internal view is the tier-3 equivalent regardless of what the tenant
  // bought (the KPI dashboard requests preview_tier=3 there), so labelling it
  // with the tenant's sold tier would contradict the data on screen. The sold
  // tier still shows inside the popover, where it is edited. Client shows the
  // previewed tier, falling back to the real one.
  const triggerLabel =
    viewMode === 'internal' ? (
      <span className="inline-flex items-center gap-1">
        Internal · all metrics
      </span>
    ) : (
      <span className="inline-flex items-center gap-1">
        Client · Tier{' '}
        {tierQuery.isLoading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          (previewTier ?? currentTier ?? '—')
        )}
      </span>
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span className="hidden max-w-[130px] truncate sm:inline">
            {triggerLabel}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="space-y-4">
          {/* Platform */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Platform
            </span>
            <Select
              value={ACTIVE_ADS_PLATFORM}
              onValueChange={handlePlatformChange}
            >
              <SelectTrigger className="h-9 w-full bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADS_PLATFORM_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              View
            </span>
            <div className="inline-flex w-full items-center rounded-md border border-border bg-muted p-1">
              <ViewToggleButton
                active={viewMode === 'internal'}
                onClick={() => handleViewChange('internal')}
                icon={<Eye className="h-3.5 w-3.5" />}
                label="Internal"
              />
              <ViewToggleButton
                active={viewMode === 'client'}
                onClick={() => handleViewChange('client')}
                icon={<Users className="h-3.5 w-3.5" />}
                label="Client"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Client view shows what the client sees.
            </p>
          </div>

          {/* Tier — view-only in internal, ephemeral preview in client view */}
          {viewMode === 'internal' ? (
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Client tier
              </span>
              <div className="flex items-center gap-2">
                {tierQuery.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Badge
                    variant="outline"
                    className={
                      (currentTier ?? 1) >= 2
                        ? 'border-teal/40 bg-teal-light/40 text-teal-dark'
                        : 'border-coral/40 bg-coral-light/40 text-coral-dark'
                    }
                  >
                    {currentTier != null
                      ? `Tier ${TIER_LABELS[currentTier] ?? currentTier}`
                      : 'Tier —'}
                  </Badge>
                )}
              </div>
              <Link
                to={ADS_SETTINGS_LINK}
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                <Settings className="h-3 w-3" />
                Set in Settings
              </Link>
            </div>
          ) : (
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Preview as Tier
              </span>
              <Select
                value={previewTier != null ? String(previewTier) : undefined}
                onValueChange={(v) => setPreviewTier(Number(v))}
              >
                <SelectTrigger className="h-9 w-full bg-background">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 · Standard</SelectItem>
                  <SelectItem value="2">2 · Managed</SelectItem>
                  <SelectItem value="3">3 · Premium</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Preview only — never changes the client&apos;s real tier. Tier 3
                adds no extra KPI metrics yet.
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ViewToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
